import { Component } from '@theme/component';
import { preventDefault } from '@theme/utilities';

const DEFAULT_BASE_WIDTH = 120;
const DEFAULT_ACTIVE_WIDTH = 400;
const DEFAULT_GAP = 0;

// Minimum movement before a touch gesture is classified as horizontal (swipe) vs vertical (scroll).
const SWIPE_AXIS_THRESHOLD = 10;
// Minimum horizontal distance for a swipe to trigger moving to the next/previous item.
const SWIPE_DISTANCE_THRESHOLD = 40;

/**
 * @typedef {object} GallerySliderRefs
 * @property {HTMLElement[]} items - The individual gallery items
 * @property {HTMLElement[]} details - The title/description panel for each item, kept in sync with `items` by index
 * @property {HTMLElement} track - The track that holds and is translated to center items
 *
 * @extends {Component<GallerySliderRefs>}
 */
export class GallerySliderComponent extends Component {
  requiredRefs = ['items', 'track', 'details'];

  /** @type {number} */
  baseWidth = DEFAULT_BASE_WIDTH;

  /** @type {number} */
  activeWidth = DEFAULT_ACTIVE_WIDTH;

  /** @type {number} */
  gap = DEFAULT_GAP;

  /** @type {number} */
  #activeIndex = 0;

  /** @type {() => void} */
  #onResize = () => this.#measureAndCenter();

  connectedCallback() {
    super.connectedCallback();

    this.baseWidth = Number(this.dataset.imageWidth) || DEFAULT_BASE_WIDTH;
    this.activeWidth = Number(this.dataset.activeImageWidth) || DEFAULT_ACTIVE_WIDTH;
    this.gap = Number(this.dataset.imageGap) || DEFAULT_GAP;

    const activeIndex = this.refs.items.findIndex((item) => item.classList.contains('active'));
    this.#activeIndex = activeIndex === -1 ? 0 : activeIndex;
    this.#measureAndCenter();

    window.addEventListener('resize', this.#onResize);
    this.refs.track.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    this.refs.track.removeEventListener('focusin', this.#onFocusIn);
  }

  /**
   * Keyboard focus on an off-screen item centers it, so the focused link stays visible (WCAG 2.4.11).
   * Mouse/touch focus is ignored, so pointer behavior stays unchanged.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches(':focus-visible')) return;

    const item = target.closest('.collection-image');
    if (!(item instanceof HTMLElement)) return;

    this.#activateItem(this.refs.items.indexOf(item));
  };

  /**
   * @param {Event} event
   */
  selectItem(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.classList.contains('active')) return;

    const newIndex = this.refs.items.indexOf(target);
    if (newIndex === -1) return;

    event.preventDefault();

    this.#activateItem(newIndex);
  }

  /**
   * Starts tracking a touch gesture on the track so it can be interpreted as a
   * swipe to the next/previous item on mobile.
   * @param {PointerEvent} event
   */
  handlePointerDown(event) {
    if (event.pointerType !== 'touch') return;
    if (this.refs.items.length <= 1) return;

    const startX = event.clientX;
    const startY = event.clientY;
    /** @type {'horizontal' | 'vertical' | null} */
    let axis = null;

    const controller = new AbortController();
    const { signal } = controller;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (!axis) {
        if (Math.abs(deltaX) < SWIPE_AXIS_THRESHOLD && Math.abs(deltaY) < SWIPE_AXIS_THRESHOLD) return;

        axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';

        // Once a horizontal swipe is confirmed, stop the eventual pointerup from also firing as a click.
        if (axis === 'horizontal') document.addEventListener('click', preventDefault, { once: true, signal });
      }

      // Prevent the page from scrolling horizontally while swiping; vertical scrolling is left untouched.
      if (axis === 'horizontal') moveEvent.preventDefault();
    };

    /** @param {PointerEvent} upEvent */
    const onPointerUp = (upEvent) => {
      controller.abort();
      if (axis !== 'horizontal') return;

      const deltaX = upEvent.clientX - startX;
      if (Math.abs(deltaX) < SWIPE_DISTANCE_THRESHOLD) return;

      this.#activateItem(this.#activeIndex + (deltaX < 0 ? 1 : -1));
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', () => controller.abort(), { signal });
  }

  /**
   * Activates the item at `newIndex`, updating the active image, details panel, and centering the track.
   * @param {number} newIndex
   */
  #activateItem(newIndex) {
    const items = this.refs.items;
    if (newIndex === this.#activeIndex || newIndex < 0 || newIndex >= items.length) return;

    const { baseWidth, activeWidth } = this.#measureWidths();

    for (const [index, item] of items.entries()) {
      item.classList.toggle('active', index === newIndex);
    }

    for (const [index, detail] of this.refs.details.entries()) {
      const isActive = index === newIndex;
      detail.classList.toggle('active', isActive);
      detail.setAttribute('aria-hidden', String(!isActive));
    }

    this.#activeIndex = newIndex;
    this.#centerTrackOn(newIndex, baseWidth, activeWidth);
  }

  /**
   * @returns {{ baseWidth: number, activeWidth: number }}
   */
  #measureWidths() {
    const items = this.refs.items;
    const activeItem = items[this.#activeIndex];
    const inactiveItem = items.find((_, index) => index !== this.#activeIndex);

    return {
      baseWidth: inactiveItem?.offsetWidth || this.baseWidth,
      activeWidth: activeItem?.offsetWidth || this.activeWidth,
    };
  }

  #measureAndCenter() {
    const { baseWidth, activeWidth } = this.#measureWidths();
    this.#centerTrackOn(this.#activeIndex, baseWidth, activeWidth);
  }

  /**
   * @param {number} activeIndex
   * @param {number} baseWidth
   * @param {number} activeWidth
   */
  #centerTrackOn(activeIndex, baseWidth, activeWidth) {
    const distanceBeforeActive = activeIndex * (baseWidth + this.gap);
    const activeCenter = distanceBeforeActive + activeWidth / 2;
    const viewportCenter = this.clientWidth / 2;

    this.refs.track.style.transform = `translateX(${viewportCenter - activeCenter}px)`;
  }
}

if (!customElements.get('gallery-slider-component')) {
  customElements.define('gallery-slider-component', GallerySliderComponent);
}
